import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import { type AdminTransactionReward } from "@/modules/recognition/admin/types";

type TransactionRewardList = Paginate<AdminTransactionReward<true, true>>;

export const transactionRewards = createQueryKeys(
  "admin-recognition-transaction-rewards",
  {
    all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  },
);

const include = "reward,user";

export const fetchTransactionRewardsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...transactionRewards.all(page, search),
    queryFn: () =>
      adminApi
        .get<TransactionRewardList>(
          `/recognition/transactions/rewards?include=${include}`,
          {
            params: { page, ...search },
          },
        )
        .then((response) => response.data),
  });

export const useFetchTransactionRewards = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchTransactionRewardsQueryOptions(page, search));

export const useApproveTransactionReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi
        .post(`/recognition/transactions/rewards/${id}/approve`)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionRewards.all._def,
      });
    },
  });
};

export const useRejectTransactionReward = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      adminApi
        .post(`/recognition/transactions/rewards/${id}/reject`)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: transactionRewards.all._def,
      });
    },
  });
};
